import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { AlertTriangle, Calendar, Plus, Trash2 } from 'lucide-react';
import {
  ApiError,
  EVENT_TYPE_LABEL,
  EVENT_TYPE_SHORT,
  api,
  canEdit,
  type AuthUser,
  type EventStatus,
  type EventSummary,
  type EventType,
  type SlotKey,
} from '@/lib/api';
import { cn } from '@/lib/utils';

const CS_TIME_OPTIONS = ['12:00', '23:00'];
const DS_TIME_OPTIONS = ['09:00', '18:00', '23:00'];
const TIME_OPTIONS: Record<Exclude<EventType, 'special_ingame'>, string[]> = {
  canyon_storm: CS_TIME_OPTIONS,
  desert_storm: DS_TIME_OPTIONS,
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function nextWeekday(targetDow: number): string {
  // 0 = Sun, 4 = Thu, 5 = Fri
  const d = new Date();
  const cur = d.getDay();
  const diff = (targetDow - cur + 7) % 7 || 7;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

const STATUS_STYLES: Record<EventStatus, string> = {
  planned: 'bg-indigo-700/50 text-indigo-100',
  completed: 'bg-emerald-700/50 text-emerald-100',
  skipped: 'bg-slate-700 text-slate-300',
};

export default function Events({ user }: { user: AuthUser }) {
  const [filter, setFilter] = useState<'upcoming' | 'past' | 'all'>('upcoming');
  const [typeFilter, setTypeFilter] = useState<'all' | EventType>('all');
  const [creating, setCreating] = useState(false);
  const editable = canEdit(user.role);

  const eventsQuery = useQuery({
    queryKey: ['events', { typeFilter }],
    queryFn: () =>
      api.listEvents(typeFilter === 'all' ? undefined : { type: typeFilter }),
  });
  const importSummaryQuery = useQuery({
    queryKey: ['events-import-summary'],
    queryFn: api.eventImportJobsSummary,
    enabled: editable,
    refetchInterval: 5000,
  });
  const pendingQuery = useQuery({
    queryKey: ['events-pending-attendance'],
    queryFn: api.listPendingAttendance,
  });

  const today = todayISO();
  const filtered = useMemo(() => {
    const rows = eventsQuery.data ?? [];
    if (filter === 'upcoming') return rows.filter((e) => e.eventDate >= today);
    if (filter === 'past') return rows.filter((e) => e.eventDate < today);
    return rows;
  }, [eventsQuery.data, filter, today]);

  return (
    <div className="space-y-4">
      <EventsTabs user={user} />
      {pendingQuery.data && pendingQuery.data.length > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-700/60 bg-amber-900/30 px-4 py-3 text-sm">
          <AlertTriangle className="text-amber-300" size={18} />
          <div className="flex-1">
            <strong>{pendingQuery.data.length}</strong>{' '}
            {pendingQuery.data.length === 1 ? 'event needs' : 'events need'} attendance marked.
          </div>
          <Link
            to={`/events/${pendingQuery.data[0].id}`}
            className="rounded border border-amber-600 px-2 py-1 text-xs hover:bg-amber-900/60"
          >
            Open oldest
          </Link>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-slate-800 bg-slate-900 p-0.5 text-sm">
          {(['upcoming', 'past', 'all'] as const).map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={cn(
                'rounded px-3 py-1.5 capitalize transition',
                filter === k ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white',
              )}
            >
              {k}
            </button>
          ))}
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as 'all' | EventType)}
          className="rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm"
        >
          <option value="all">All types</option>
          <option value="canyon_storm">Canyon Storm</option>
          <option value="desert_storm">Desert Storm</option>
          <option value="special_ingame">Special event</option>
        </select>
        <div className="ml-auto">
          {editable && (
            <button
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-1 rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500"
            >
              <Plus size={14} /> Create event
            </button>
          )}
        </div>
      </div>

      {eventsQuery.isLoading ? (
        <div className="text-center text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-8 text-center text-slate-500">
          No events {filter !== 'all' ? filter : ''}.
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((e) => (
            <EventCard
              key={e.id}
              event={e}
              importSummary={importSummaryQuery.data?.byEvent[e.id]}
            />
          ))}
        </ul>
      )}

      {creating && <CreateEventModal onClose={() => setCreating(false)} />}
    </div>
  );
}

export function EventsTabs({ user }: { user?: AuthUser | null }) {
  const tabs: { to: string; label: string; end: boolean }[] = [
    { to: '/events', label: 'Events', end: true },
    { to: '/events/stats', label: 'Stats', end: false },
  ];
  if (user && canEdit(user.role)) {
    tabs.push({ to: '/events/vs', label: 'VS', end: false });
  }
  return (
    <div className="inline-flex rounded-lg border border-slate-800 bg-slate-900 p-0.5 text-sm">
      {tabs.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) =>
            cn(
              'rounded px-3 py-1.5 transition',
              isActive ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white',
            )
          }
        >
          {t.label}
        </NavLink>
      ))}
    </div>
  );
}

type WorkflowStep = {
  label: string;
  idx: number;
  tone: 'done' | 'active' | 'pending';
};

function computeWorkflowStep(event: EventSummary): WorkflowStep {
  const c = event.counts;
  const anyApplied = c.appliedA + c.appliedB > 0;
  const anyAssigned = c.startersA + c.subsA + c.startersB + c.subsB > 0;
  const planningDone = !!event.planningDoneAt;
  const attendancePending = c.pendingAttendance > 0;
  const eventIsPast = event.eventDate <= new Date().toISOString().slice(0, 10);

  if (event.status === 'skipped') {
    return { label: 'Skipped', idx: 0, tone: 'pending' };
  }
  if (event.status === 'completed' && !attendancePending) {
    return { label: 'Done', idx: 5, tone: 'done' };
  }
  if (planningDone && eventIsPast) {
    return {
      label: attendancePending ? 'Attendance' : 'Done',
      idx: 5,
      tone: attendancePending ? 'active' : 'done',
    };
  }
  if (planningDone) {
    return { label: 'Planning done', idx: 4, tone: 'done' };
  }
  if (anyAssigned) {
    return { label: 'Planning', idx: 4, tone: 'active' };
  }
  if (anyApplied) {
    return { label: 'Slots', idx: 3, tone: 'active' };
  }
  return { label: 'Candidates', idx: 2, tone: 'active' };
}

const STEP_TONE: Record<WorkflowStep['tone'], string> = {
  done: 'bg-emerald-700/50 text-emerald-100',
  active: 'bg-amber-700/50 text-amber-100',
  pending: 'bg-slate-800 text-slate-400',
};

function EventCard({
  event,
  importSummary,
}: {
  event: EventSummary;
  importSummary?: { pending: number; ready: number; failed: number };
}) {
  const slotA = event.slots.find((s) => s.slotKey === 'A');
  const slotB = event.slots.find((s) => s.slotKey === 'B');
  const isSpecial = event.type === 'special_ingame';
  const step = !isSpecial ? computeWorkflowStep(event) : null;
  return (
    <li>
      <Link
        to={`/events/${event.id}`}
        className="block rounded-lg border border-slate-800 bg-slate-900/40 p-4 transition hover:bg-slate-900"
      >
        <div className="flex flex-wrap items-center gap-3">
          <Calendar size={18} className="text-slate-500" />
          <div className="font-medium">
            {isSpecial && event.customName
              ? event.customName
              : EVENT_TYPE_LABEL[event.type]}
          </div>
          {isSpecial && (
            <span className="rounded bg-fuchsia-700/50 px-2 py-0.5 text-xs text-fuchsia-100">
              Special
            </span>
          )}
          <div className="text-sm text-slate-400">
            {event.eventDate}
            {isSpecial && event.startsAt ? ` · ${event.startsAt}` : ''}
          </div>
          {event.status !== 'planned' && (
            <span
              className={cn(
                'rounded px-2 py-0.5 text-xs capitalize',
                STATUS_STYLES[event.status],
              )}
            >
              {event.status}
            </span>
          )}
          {step && (
            <span
              title={`Workflow step ${step.idx}/5`}
              className={cn(
                'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium',
                STEP_TONE[step.tone],
              )}
            >
              <span className="text-[10px] opacity-70">{step.idx}/5</span>
              {step.label}
            </span>
          )}
          {!isSpecial && event.counts.pendingAttendance > 0 && (
            <span className="rounded bg-amber-700/50 px-2 py-0.5 text-xs text-amber-100">
              {event.counts.pendingAttendance} pending
            </span>
          )}
          {importSummary && importSummary.ready > 0 && (
            <span
              className="rounded bg-emerald-800/60 px-2 py-0.5 text-xs text-emerald-100"
              title="Video import ready for preview"
            >
              📼 {importSummary.ready} import ready
            </span>
          )}
          {importSummary && importSummary.pending > 0 && (
            <span
              className="rounded bg-indigo-800/60 px-2 py-0.5 text-xs text-indigo-100"
              title="Video import running"
            >
              📼 {importSummary.pending} processing
            </span>
          )}
          {importSummary && importSummary.failed > 0 && (
            <span
              className="rounded bg-red-800/60 px-2 py-0.5 text-xs text-red-100"
              title="Video import failed"
            >
              📼 {importSummary.failed} failed
            </span>
          )}
          {!isSpecial && (
            <div className="ml-auto text-xs text-slate-400">
              Slot A {slotA?.startsAtServer ?? '—'} · Slot B {slotB?.startsAtServer ?? '—'}
            </div>
          )}
        </div>
        {!isSpecial && (
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <SlotProgress label="A" counts={event.counts} which="A" />
            <SlotProgress label="B" counts={event.counts} which="B" />
          </div>
        )}
      </Link>
    </li>
  );
}

function SlotProgress({
  label,
  counts,
  which,
}: {
  label: string;
  counts: EventSummary['counts'];
  which: SlotKey;
}) {
  const starters = which === 'A' ? counts.startersA : counts.startersB;
  const subs = which === 'A' ? counts.subsA : counts.subsB;
  const applied = which === 'A' ? counts.appliedA : counts.appliedB;
  return (
    <div className="rounded bg-slate-950/60 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">Slot {label}</div>
      <div className="tabular-nums">
        Starters <strong>{starters}/20</strong> · Subs <strong>{subs}/10</strong>
        <span className="ml-2 text-xs text-slate-400">{applied} applied</span>
      </div>
    </div>
  );
}

function CreateEventModal({ onClose }: { onClose: () => void }) {
  const [type, setType] = useState<EventType>('canyon_storm');
  const [eventDate, setEventDate] = useState(() => nextWeekday(4));
  const [timeA, setTimeA] = useState('12:00');
  const [timeB, setTimeB] = useState('23:00');
  const [customName, setCustomName] = useState('');
  const [specialStartsAt, setSpecialStartsAt] = useState('');
  const [teamCount, setTeamCount] = useState(4);
  const [teamShares, setTeamShares] = useState<number[]>([1, 1, 1, 1]);
  const [teamCapacities, setTeamCapacities] = useState<number[] | null>(null);
  const [teamNames, setTeamNames] = useState<string[]>(['', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const activePlayersQuery = useQuery({
    queryKey: ['roster'],
    queryFn: () => api.listPlayers(),
    enabled: type === 'special_ingame',
  });
  const activeCount = (activePlayersQuery.data ?? []).filter((p) => p.active).length;

  const defaultCapacities = useMemo(() => {
    if (activeCount === 0) return Array.from({ length: teamCount }, () => 0);
    const per = Math.floor(activeCount / teamCount);
    const rem = activeCount - per * teamCount;
    return Array.from({ length: teamCount }, (_, i) => per + (i === 0 ? rem : 0));
  }, [activeCount, teamCount]);
  const capacities = teamCapacities ?? defaultCapacities;

  const shareSum = teamShares.reduce((s, v) => s + v, 0) || 1;

  const handleTypeChange = (t: EventType) => {
    setType(t);
    if (t === 'canyon_storm') {
      setEventDate(nextWeekday(4));
      const opts = TIME_OPTIONS.canyon_storm;
      setTimeA(opts[0]);
      setTimeB(opts[opts.length - 1]);
    } else if (t === 'desert_storm') {
      setEventDate(nextWeekday(5));
      const opts = TIME_OPTIONS.desert_storm;
      setTimeA(opts[0]);
      setTimeB(opts[opts.length - 1]);
    } else {
      setEventDate(todayISO());
    }
  };

  const setTeamCountSafely = (n: number) => {
    const next = Math.max(2, Math.min(8, n));
    setTeamCount(next);
    setTeamShares((prev) => {
      const arr = prev.slice(0, next);
      while (arr.length < next) arr.push(1);
      return arr;
    });
    setTeamNames((prev) => {
      const arr = prev.slice(0, next);
      while (arr.length < next) arr.push('');
      return arr;
    });
    setTeamCapacities(null);
  };

  const csDsMutation = useMutation({
    mutationFn: () =>
      api.createEvent({
        type: type as 'canyon_storm' | 'desert_storm',
        eventDate,
        slots: [
          { slotKey: 'A', startsAtServer: timeA },
          { slotKey: 'B', startsAtServer: timeB },
        ],
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] });
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Failed to create event'),
  });

  const specialMutation = useMutation({
    mutationFn: () =>
      api.createSpecialEvent({
        customName: customName.trim(),
        eventDate,
        startsAt: specialStartsAt.trim() === '' ? null : specialStartsAt,
        teams: Array.from({ length: teamCount }, (_, i) => ({
          name: teamNames[i]?.trim() ? teamNames[i].trim() : null,
          capacity: capacities[i] ?? 0,
          strengthShare: teamShares[i] ?? 1,
        })),
      }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['events'] });
      onClose();
      navigate(`/events/${created.event.id}`);
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Failed to create event'),
  });

  const isSpecial = type === 'special_ingame';
  const opts = !isSpecial ? TIME_OPTIONS[type] : [];

  const submit = () => {
    setError(null);
    if (isSpecial) {
      if (!customName.trim()) {
        setError('Please enter a name for the event');
        return;
      }
      if (capacities.some((c) => c <= 0)) {
        setError('Every team must have capacity ≥ 1');
        return;
      }
      specialMutation.mutate();
    } else {
      csDsMutation.mutate();
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl space-y-4 overflow-y-auto rounded-lg border border-slate-800 bg-slate-950 p-6 shadow-2xl">
        <h2 className="text-lg font-semibold">Create event</h2>
        <Field label="Type">
          <select
            value={type}
            onChange={(e) => handleTypeChange(e.target.value as EventType)}
            className="input"
          >
            <option value="canyon_storm">Canyon Storm (CS)</option>
            <option value="desert_storm">Desert Storm (DS)</option>
            <option value="special_ingame">Special ingame event</option>
          </select>
        </Field>
        {isSpecial && (
          <Field label="Name">
            <input
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="e.g. Alliance Duel"
              className="input"
            />
          </Field>
        )}
        <Field label="Date">
          <input
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            className="input"
          />
        </Field>
        {isSpecial && (
          <Field label="Time (optional)">
            <input
              type="time"
              value={specialStartsAt}
              onChange={(e) => setSpecialStartsAt(e.target.value)}
              className="input"
            />
          </Field>
        )}
        {!isSpecial ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Slot A time">
              <select value={timeA} onChange={(e) => setTimeA(e.target.value)} className="input">
                {opts.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Slot B time">
              <select value={timeB} onChange={(e) => setTimeB(e.target.value)} className="input">
                {opts.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-end gap-3">
              <Field label="Number of teams">
                <input
                  type="number"
                  min={2}
                  max={8}
                  value={teamCount}
                  onChange={(e) => setTeamCountSafely(Number(e.target.value) || 2)}
                  className="input w-24"
                />
              </Field>
              <button
                type="button"
                onClick={() => {
                  setTeamShares(Array.from({ length: teamCount }, () => 1));
                  setTeamCapacities(null);
                }}
                className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
              >
                Reset to equal split
              </button>
              <div className="ml-auto text-xs text-slate-400">
                Active players: <strong>{activeCount}</strong>
              </div>
            </div>
            <div className="rounded border border-slate-800 bg-slate-900/40 p-3">
              <div className="mb-2 grid grid-cols-[24px_1fr_100px_100px] items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500">
                <div>#</div>
                <div>Name (optional)</div>
                <div>Capacity</div>
                <div>Share</div>
              </div>
              <div className="space-y-2">
                {Array.from({ length: teamCount }, (_, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[24px_1fr_100px_100px] items-center gap-2"
                  >
                    <div className="text-sm text-slate-400">{i + 1}</div>
                    <input
                      value={teamNames[i] ?? ''}
                      onChange={(e) => {
                        const next = teamNames.slice();
                        next[i] = e.target.value;
                        setTeamNames(next);
                      }}
                      placeholder={`Team ${i + 1}`}
                      className="input"
                    />
                    <input
                      type="number"
                      min={1}
                      value={capacities[i] ?? 0}
                      onChange={(e) => {
                        const v = Number(e.target.value) || 0;
                        const base = teamCapacities ?? defaultCapacities;
                        const next = base.slice();
                        next[i] = v;
                        setTeamCapacities(next);
                      }}
                      className="input"
                    />
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={teamShares[i] ?? 1}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        const next = teamShares.slice();
                        next[i] = Number.isFinite(v) ? v : 0;
                        setTeamShares(next);
                      }}
                      className="input"
                    />
                  </div>
                ))}
              </div>
              <div className="mt-2 text-xs text-slate-400">
                Shares are normalized. Sum = {shareSum} ({teamShares
                  .map((s) => `${Math.round((s / shareSum) * 100)}%`)
                  .join(' · ')}
                )
              </div>
            </div>
          </div>
        )}
        {error && <div className="text-sm text-red-400">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="rounded border border-slate-700 px-3 py-1.5 text-sm hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={csDsMutation.isPending || specialMutation.isPending}
            className="rounded bg-indigo-600 px-3 py-1.5 text-sm hover:bg-indigo-500 disabled:opacity-50"
          >
            {csDsMutation.isPending || specialMutation.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
        <Style />
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="text-xs text-slate-400">{label}</span>
      {children}
    </label>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        width: 100%;
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
      }
      .input:focus { outline: none; border-color: rgb(99 102 241 / 1); }
    `}</style>
  );
}
